// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { CustomerFormGroup, DealerFormGroup, DealTypeInputs } from '@interfaces/create-deal-inputs.interface';
import { AuditTrail, Deal, DealData, DealStatuses, JacketType, VehicleType } from '@interfaces/deal.interfaces';
import { SelectedTemplateGroup } from '@interfaces/document.interfaces';
import { User, UserAuditTrail, UserTypes } from '@interfaces/user.interfaces';
import { ReasonForRequestStatus, Vehicle, VehicleCreation } from '@interfaces/vehicle.interfaces';
import { UserService } from '@services/user.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { RpcApiResponse } from '@interfaces/api.interfaces';
import { ErrorService } from './error.service';

@Injectable({
    providedIn: 'root',
})
export class DealsService {
    selectedDealId: number;
    currentDealId: number;
    dealdata: Observable<DealData>;
    deal: Deal;
    isDealsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    isDealLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private readonly PAGE_SIZE_KEY = 'tablePageSizes';

    constructor(
        private readonly apiService: ApiService,
        private readonly userService: UserService,
        private errorService: ErrorService
    ) {}

    getTablePageSize(jacketType: JacketType): number {
        const sizes = this.getAllTablePageSizes();
        return sizes[jacketType] ?? 25;
    }

    setTablePageSize(jacketType: JacketType, pageSize: number): void {
        const sizes = this.getAllTablePageSizes();
        sizes[jacketType] = pageSize;
        sessionStorage.setItem(this.PAGE_SIZE_KEY, JSON.stringify(sizes));
    }

    private getAllTablePageSizes(): { [key: string]: number } {
        const raw = sessionStorage.getItem(this.PAGE_SIZE_KEY);
        return raw ? JSON.parse(raw) : {};
    }

    getAuditTrail(dealerId: number, dealId: number, showDeletedDocuments: boolean, documentId?: number): Observable<AuditTrail> {
        const params = documentId
            ? `dealerId=${dealerId}&dealId=${dealId}&showDeletedDocuments=${showDeletedDocuments}&documentId=${documentId}`
            : `dealerId=${dealerId}&dealId=${dealId}&showDeletedDocuments=${showDeletedDocuments}`;
        return this.apiService.getV2$<AuditTrail>(`api/deal/AuditTrailForDocuments?${params}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response?.data)
        );
    }

    getNotesAuditTrail(dealId: number): Observable<Array<AuditTrail>> {
        return this.apiService.getV2$<Array<AuditTrail>>(`api/deal/AuditTrailForNotes?dealId=${dealId}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response?.data)
        );
    }

    getDealerInfoAuditTrail(dealId: number): Observable<AuditTrail> {
        const params = `dealId=${dealId}`;
        return this.apiService.getV2$<AuditTrail>(`api/deal/AuditTrailForDealerInfo?${params}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response?.data)
        );
    }

    getDealsByType$(jacketTypeId: JacketType, pageSize?: number): Observable<DealData> {
        return this.getDeals$(undefined, undefined, pageSize ?? undefined, undefined, jacketTypeId);
    }

    getDeals$(
        categories: DealStatuses[] = [DealStatuses.Open],
        query = '',
        pageSize = 25,
        offset = 0,
        jacketTypeId: JacketType = null,
        userId: number = null,
        sortBy = '',
        sortAsc = false
    ): Observable<DealData> {
        const userIdParam = null === userId ? '' : '&userId=' + userId;
        const jacketTypeIdParam = null === jacketTypeId ? '' : '&jacketTypeId=' + jacketTypeId;
        const statusIds = categories.join(',');
        const sanitizedQuery = encodeURIComponent(query.trim());
        return this.apiService
            .getV2$<DealData>(
                `api/deal/list?pageOffset=${offset}&pageSize=${pageSize}&query=${sanitizedQuery}&categories=${statusIds}&dealerId=${this.userService.user.dealerId}${jacketTypeIdParam}${userIdParam}&sortBy=${sortBy}&sortAsc=${sortAsc}`
            )
            .pipe(
                map((response) => {
                    return response?.data || { count: 0, deals: [] };
                }),
                tap(() => {
                    this.isDealsLoaded$.next(true);
                })
            );
    }

    getDealRootLink(isBuyer: boolean): string {
        let root = '/dashboard/deals';

        if (isBuyer) {
            root = '/buyer';
        }

        return root;
    }

    getDeal$(dealId: number, forceFreshLoad = false): Observable<Deal> {
        if (!this.currentDealId || this.currentDealId !== dealId || forceFreshLoad) {
            return this.apiService.getV1$<Deal>(`api/deal/${dealId}/dealerId/${this.userService.user.dealerId}`).pipe(
                catchError(() => {
                    this.errorService.showError({
                        title: 'Jacket Not Found',
                        message:
                            'The EZ DOCS jacket you are trying to open cannot be found. Check to make sure the URL is correct. If you have access to multiple dealerships, click on Change Dealership to navigate to the correct location. If you do not have access to the other location, contact your system administrator.',
                    });
                    return of(null);
                }),
                map((response) => {
                    this.currentDealId = dealId;
                    this.deal = response?.data;
                    return response?.data;
                }),
                tap(() => {
                    this.isDealLoaded$.next(true);
                })
            );
        } else {
            return of(this.deal);
        }
    }

    setCurrentDealId(dealId?: number): void {
        this.currentDealId = dealId;
    }

    deleteDeal$(dealId: number, deleteReason: string, jacketType: JacketType): Observable<Deal> {
        return this.apiService
            .deleteV2$<Deal>(`api/deal/${dealId}/dealerId/${this.userService.user.dealerId}/${encodeURIComponent(deleteReason)}/${jacketType}`)
            .pipe(map((response) => response?.data));
    }

    restoreDeal$(dealId: number, jacketType: JacketType): Observable<Deal> {
        return this.apiService.postV3$<Deal>(`api/deal/${dealId}/dealerId/${this.userService.user.dealerId}/restore/${jacketType}`, null).pipe(
            catchError((httpResponse) => {
                //Rethrow error for component to handle
                throw httpResponse;
            }),
            map((response) => {
                return response.data;
            })
        );
    }

    createDeal$(
        dealTypeInputs: DealTypeInputs,
        buyer: CustomerFormGroup,
        vehicle: VehicleCreation,
        dealerId: number,
        coBuyer?: CustomerFormGroup,
        dealer?: DealerFormGroup,
        customTemplateGroupDeal?: SelectedTemplateGroup,
        tradeInVehicle?: Vehicle,
        leadId?: number
    ): Observable<Deal> {
        return this.apiService
            .postV3$<Deal>(
                'api/deal',
                this.transformDealAddApi(
                    dealTypeInputs,
                    buyer,
                    dealerId,
                    coBuyer,
                    dealer,
                    customTemplateGroupDeal,
                    vehicle,
                    vehicle.manuallyCreated,
                    tradeInVehicle,
                    leadId
                ),
                null,
                false
            )
            .pipe(
                catchError((httpResponse) => {
                    //Rethrow error for component to handle
                    throw httpResponse;
                }),
                map((response) => response.data)
            );
    }

    updateDocumentJacketToDealJacket$(
        dealId: number,
        dealTypeInputs: DealTypeInputs,
        buyer: CustomerFormGroup,
        vehicle: VehicleCreation,
        dealerId: number,
        coBuyer?: CustomerFormGroup,
        dealer?: DealerFormGroup,
        customTemplateGroupDeal?: SelectedTemplateGroup,
        tradeInVehicle?: Vehicle,
        leadId?: number
    ): Observable<Deal> {
        return this.apiService
            .postV3$<Deal>(
                `api/deal/updateJacketType/${dealId}`,
                this.transformDealAddApi(
                    dealTypeInputs,
                    buyer,
                    dealerId,
                    coBuyer,
                    dealer,
                    customTemplateGroupDeal,
                    vehicle,
                    vehicle.manuallyCreated,
                    tradeInVehicle,
                    leadId
                )
            )
            .pipe(
                catchError((httpResponse) => {
                    //Rethrow error for component to handle
                    throw httpResponse;
                }),
                map((response) => response.data)
            );
    }

    createDocumentJacket$(request: any): Observable<Deal> {
        return this.apiService.postV3$('api/deal/jacket/document', request).pipe(map((response: any) => response));
    }

    createStorageJacket$(request: any): Observable<Deal> {
        return this.apiService.postV2$('api/deal/jacket/storage', request).pipe(map((response: any) => response));
    }

    updateStorageJacket$(request: any): Observable<RpcApiResponse<Deal>> {
        return this.apiService.putV2$('api/deal/jacket/storage', request).pipe(map((response: any) => response));
    }

    updateDealInfo(
        dealerId: number,
        dealId: number,
        dealerDealNumber?: string,
        reasonForRequest?: ReasonForRequestStatus,
        dealerContactId?: number, // This is "Finance Manager" in the UI
        deleteReason?: string
    ): Observable<Deal> {
        return this.apiService
            .putV2$('api/Deal/updateDeal', {
                dealerId: dealerId,
                dealId: dealId,
                dealerDealNumber: dealerDealNumber,
                dealerContactId: dealerContactId,
                reasonForRequest: reasonForRequest,
                deleteReason: deleteReason,
            })
            .pipe(
                map((response: { data: Deal; error?; message? }) => {
                    return response.data;
                })
            );
    }

    updateDealNotes(dealId: number, dealerId: number, notes?: string): Observable<Deal> {
        return this.apiService
            .postV3$<Deal>('api/deal/updateNotes', {
                dealId: dealId,
                notes: notes,
                dealerId: dealerId,
            })
            .pipe(
                catchError((httpResponse) => {
                    //Rethrow error for component to handle
                    throw httpResponse;
                }),
                map((response) => {
                    return response.data;
                })
            );
    }

    closeDeal$(dealId: number, jacketType: JacketType, force = false): Observable<string> {
        // remove reference to the dealId, to force this.getDeal$() to fetch a fresh version the next time a deal is needed
        this.currentDealId = null;

        return this.apiService
            .putV3$<string>(`api/deal/${dealId}/close/${this.userService.user.dealerId}/${jacketType}${force ? '?force=' + force : ''}`, null)
            .pipe(
                catchError((httpResponse) => {
                    //Rethrow error for component to handle
                    throw httpResponse;
                }),
                map((response) => {
                    return response.data;
                })
            );
    }

    reopenDeal$(dealId: number): Observable<Deal> {
        return this.apiService.postV3$<Deal>(`api/deal/reopenDeal/${dealId}/${this.userService.user.dealerId}`, null).pipe(
            catchError((httpResponse) => {
                //Rethrow error for component to handle
                throw httpResponse;
            }),
            map((response) => {
                return response.data;
            })
        );
    }

    archiveDeal$(dealId: number, dealerId: number, jacketType: JacketType, archive: boolean): Observable<Deal> {
        return this.apiService.postV3$<Deal>(`api/deal/${dealId}/dealer/${dealerId}/${archive}/${jacketType}`, null).pipe(
            map((response) => {
                const deal = response.data;
                this.deal = deal;
                this.setCurrentDealId(deal?.dealId);
                return deal;
            })
        );
    }

    sendNotification(dealId: number, customerType: number): Observable<any> {
        return this.apiService.getV2$(`api/deal/${dealId}/document/notification/${customerType}/${this.userService.user.dealerId}`, null).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response.data)
        );
    }

    saveVehicle(dealId: number, vehicle: any, type: VehicleType = VehicleType.Vehicle): Observable<Vehicle> {
        return this.apiService
            .putV2$<Vehicle>(
                'api/deal/updateVehicle',
                {
                    dealId: dealId,
                    vehicleInformation: vehicle,
                    vehicleType: type,
                },
                null
            )
            .pipe(map((response) => response?.data));
    }

    addCoBuyerToJacket(dealId: number, cobuyer: User): Observable<Deal> {
        return this.apiService
            .postV2$<Deal>(`api/deal/${dealId}/addCobuyer/${this.userService.user.dealerId}`, cobuyer, null)
            .pipe(map((response) => response.data));
    }

    removeCoBuyerFromJacket(dealId: number, cobuyerId: number): Observable<Deal> {
        return this.apiService
            .postV2$<Deal>(`api/deal/${dealId}/removeCobuyer/${this.userService.user.dealerId}`, cobuyerId, null)
            .pipe(map((response) => response.data));
    }

    swapBuyerAndCoBuyer(dealId: number): Observable<Deal> {
        return this.apiService
            .postV2$<Deal>(`api/deal/${dealId}/SwapBuyerAndCobuyer/${this.userService.user.dealerId}`, null, null)
            .pipe(map((response) => response.data));
    }

    getAuditTrailForVehicle(dealId: number, vehicleType: VehicleType): Observable<AuditTrail[]> {
        return this.apiService
            .getV1$<AuditTrail[]>(`api/vehicle/AuditTrailForVehicle?dealId=${dealId}${vehicleType === VehicleType.TradeIn ? '&tradeIn=true' : ''}`)
            .pipe(
                catchError((err) => {
                    //Made graceful while keeping debugging alive. (The OG error is still obtainable)
                    err.error = { ...err.error, message: 'No audit information is available at this time' };
                    // rethrow errors for further handling
                    throw this.apiService.apiErrorHandler(err);
                }),
                map((response) => response?.data || [])
            );
    }

    getAuditTrailForUser(userId: number, dealId: number, userType: UserTypes): Observable<UserAuditTrail[]> {
        return this.apiService.getV2$<UserAuditTrail[]>(`api/deal/AuditTrailForUser?userId=${userId ?? ''}&dealId=${dealId}&userType=${userType}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response?.data || [])
        );
    }

    getAuditTrailForDeal(dealerId: number, dealId: number): Observable<AuditTrail[]> {
        return this.apiService.getV2$<AuditTrail[]>(`api/deal/AuditTrailForDeal?dealerId=${dealerId}&dealId=${dealId}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => response?.data || [])
        );
    }

    private transformDealAddApi(
        dealTypeInputs: DealTypeInputs,
        buyer: CustomerFormGroup,
        dealerId: number,
        coBuyer?: CustomerFormGroup,
        dealer?: DealerFormGroup,
        customTemplateGroupDeal?: SelectedTemplateGroup,
        vehicleInformation?: VehicleCreation,
        manuallyCreated?: boolean,
        tradeInVehicle?: Vehicle,
        leadId?: number
    ) {
        const body = {
            dealerId: dealerId,
            buyer: buyer,
            coBuyer: coBuyer,
            dealerDealNumber: dealer,
            vin: vehicleInformation.vin,
            deal: {
                isNew: dealTypeInputs.isNew,
                isBuy: dealTypeInputs.isBuy,
                isCash: dealTypeInputs.isCash,
                isTradeIn: dealTypeInputs.isTradeIn,
                isLeaseTurnIn: dealTypeInputs.isLeaseTurnIn,
            },
            customTemplateGroupDeal: customTemplateGroupDeal,
            vehicleInformation: vehicleInformation,
            tradeInVehicle,
            manuallyCreated: manuallyCreated,
            leadId,
        };

        if (!coBuyer) {
            delete body.coBuyer;
        }

        return body;
    }
    submitForValidation$(dealerId: number, dealId: number, documentId: number) {
        return this.apiService.postV3$(`api/deal/${dealId}/file/intellicheck/${documentId}/${dealerId}/process`, null).pipe();
    }
}
